import { FC, ReactNode, useRef, useState, useEffect } from 'react'
import cn from 'classnames'

import IconLoading from 'images/icon-loader-blue.svg'
import onlyNumberChanger from 'utils/only-numbers-value'
import { formatNumber } from 'utils/formatters'

import Input from 'components/app/input'
import { NetworkLabel } from 'components/ui/network-label'
import { ICurrency } from 'store/fetchers/currencies'

import { useTranslation } from 'utils/use-translation'

import IconInputArrow from '../../icons/icon-input-arrow.svg'

import useOnClickOutside from '../../../../../../hooks/use-on-click-outside'

import { countDecimalPlaces } from '../../../../../../utils/count-decimal-places'

import { floorNumber } from '../../../../../../utils/floor-number'

import { adjustCursorPosition } from '../../../../../../utils/input-cursor'

import { CoinList } from '../../../../calculator/components/coins-dropdown'

import styles from './styles.module.scss'

interface Props {
  onChangeAmount: (value: string) => void
  onChangeCoin: (coin: ICurrency) => void
  value: string
  label: string
  coin?: ICurrency
  list: ICurrency[]
  children: ReactNode
  isLoading?: boolean
  onEnter?: () => void
  isStableFirst?: boolean
  className?: string
  isError?: boolean
  isExternalWallet?: boolean
  onWalletSwitch?: (isExternalWallet: boolean) => void
  hasSwitchWalletBtn?: boolean
  balance?: number
  isControledValue?: boolean
  inputMode?: 'text' | 'email' | 'search' | 'none' | 'tel' | 'url' | 'numeric' | 'decimal'
}
const InputCalculator: FC<Props> = ({
  onChangeAmount,
  onChangeCoin,
  value,
  label,
  coin,
  list = [],
  children,
  isLoading,
  onEnter,
  className = null,
  isStableFirst,
  isExternalWallet,
  onWalletSwitch,
  hasSwitchWalletBtn,
  balance,
  inputMode = 'text',
}) => {
  const { t } = useTranslation()

  const [isVisibleList, setVisibleList] = useState(false)
  const [formattedValue, setFormattedValue] = useState(value)
  const ref = useRef(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const coinListRef = useRef(null)

  const formattedLabel = label.replace(' ', '_').toLowerCase()

  useEffect(() => {
    if (value !== null && value !== undefined) {
      const strVavue = value?.toString()
      setFormattedValue(formatNumber(strVavue.replace(/\s/g, '')))
    }
  }, [value])

  const handleChangeAmount = (event) => {
    if (coin) {
      const rawValue: string = event.target.value.replace(/\s/g, '')

      const parts = rawValue.split('.')
      if (parts[1]?.length > coin.decimal_places) {
        return
      }

      let cursorPosition = event.target.selectionStart
      let newValue = ''

      if (event.nativeEvent.inputType === 'deleteContentBackward') {
        cursorPosition += 1
      } else if (event.nativeEvent.inputType === 'deleteContentForward') {
        cursorPosition += 1
      } else {
        cursorPosition -= 1
      }

      onlyNumberChanger(rawValue, (newRawValue) => {
        newValue = newRawValue

        const newFormattedValue = formatNumber(newValue)

        setTimeout(() => {
          if (inputRef.current) {
            const newCursorPosition = cursorPosition - (formattedValue.length - newFormattedValue.length)
            inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
          }
        }, 0)
      })
      onlyNumberChanger(rawValue, onChangeAmount)
    }
  }

  const handleCoinListClose = () => {
    setVisibleList(false)
  }

  const handleChooseCoin = (coin) => {
    onChangeCoin(coin)
    handleCoinListClose()
  }

  useOnClickOutside(ref, handleCoinListClose)

  const switchWallet = () => {
    onWalletSwitch(!isExternalWallet)
  }

  const handleOpenList = () => {
    setVisibleList((st) => {
      const newState = !st
      if (newState && coinListRef.current) {
        coinListRef.current.resetSearch()
      }
      return newState
    })
  }

  return (
    <div className={cn(styles.form, className)}>
      <div className={styles.title}>
        <label className={cn(styles.text, styles.textSmall, styles.textGray)} htmlFor={formattedLabel}>
          {label}
        </label>
        {hasSwitchWalletBtn && (
          <button onClick={switchWallet} className={styles.switchWallet}>
            {isExternalWallet ? <>{t('switch_to_coinrabbit_wallet')}</> : <>{t('switch_to_external_wallet')}</>}
          </button>
        )}
      </div>

      <div className={cn(styles.formItem, { [styles.formItem_visibleList]: isVisibleList })}>
        <div className={styles.input}>
          <Input
            ref={inputRef}
            className={cn(styles.textArea, styles.text, styles.textBig, styles.textBlack)}
            onEnter={onEnter}
            value={isLoading ? '' : formattedValue}
            onChange={handleChangeAmount}
            onKeyDown={adjustCursorPosition}
            onMouseUp={adjustCursorPosition}
            id={formattedLabel}
            inputMode={inputMode}
          />
          {isLoading && <img src={IconLoading} className={styles.loader} alt="loading" />}
          {children}
        </div>

        <div ref={ref} className={cn(styles.select)}>
          <div className={styles.openListButton} onClick={handleOpenList}>
            {coin && coin.code && (
              <>
                <div className={styles.currency}>
                  <img className={styles.currencyIcon} alt={coin.code} src={coin.logo_url} />
                  <div className={styles.coin}>
                    <div className={styles.coin__info}>
                      <p className={cn(styles.coin__ticker, { [styles.coin__ticker_long]: coin.code.length > 4 })}>
                        {coin.code}
                      </p>
                      <NetworkLabel currency={coin} className={styles.coin__network} />
                    </div>

                    <p className={styles.coin__name}>
                      {!isExternalWallet ? `${t('balance')}: ` : ''}
                      {!isExternalWallet
                        ? balance
                          ? balance < 1 && countDecimalPlaces(balance) > 6
                            ? floorNumber(balance, 6)
                            : floorNumber(balance, 6)
                          : '0'
                        : coin.name}
                    </p>
                  </div>
                </div>

                <div className={cn(styles.arrow, { [styles.arrowRotated]: isVisibleList })}>
                  {list.length > 0 && <img src={IconInputArrow} alt="IconInputArrow" width={15} />}
                </div>
              </>
            )}
          </div>

          <CoinList
            ref={coinListRef}
            showBalance
            listFullWidth
            isStableFirst={isStableFirst}
            list={list}
            selected={coin}
            onChoose={handleChooseCoin}
            isVisibleList={isVisibleList}
          />
        </div>
      </div>
    </div>
  )
}

export default InputCalculator
